import { useConfiguratorState, useModelName } from '../app/configurator-logic';
import { ConfiguratorStages } from './configurator-stages';
import { SidebarFixed, SidebarScrollable } from './sidebar';
import { ConfiguratorQuestion } from './configurator-question';
import { SuggestedPrice } from './suggested-price';
import { blockSeparator } from '../controls/util-styles';
import { DebugValues } from './debug-info';
import { COMPOSE_STAGE, SUMMARY_STAGE } from '../navigation';
import { Summary } from './summary';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { addAttachSelector, contentSelector, configuratorActions, modeSelector, 
         showElementChooserSelector, selectedValuesSelector, 
         selectedImplicitValuesSelector, currentSceneValuesSelector, sceneSelector}
         from '../app/configurator-slice';
import { showDebugSelector } from '../app/dev-slice';
import ViewControl from './view-controls';
import DirectionButton from '../controls/directionButton/direction-button';
import { Popup } from '../controls/popup/popup';
import { PricesCard } from '../controls/pricesCard/prices-card';
import { ConfiguratorHeader } from './configurator-header/configurator-header';
import { ElementChooser } from './element-chooser/element-chooser';
import { useMessages2 } from '../hooks/useLanguage';
import { ButtonBar } from '../controls/buttons';
import { showPopup } from '../app/popup-slice';
import { Checkbox } from '../controls/checkbox/checkbox';
import { MAIN_SCENE } from '../app/types/composition';
import { ConfiguratorLayout, cfgLayoutStyles } from './configurator-layout/configurator-layout';
import { isRedirectedFromSalesSelector } from '../app/main-slice';
import { viewerLoadingSelector } from '../app/viewer-slice';
import { catalogSelector, countersUseSelector, requiredAttachPendingSelector, stagesSelector } from '../app/configurator-selectors';

import styles from './configurator-root.module.css';

export const ConfiguratorRoot = ({}) => {

    const {
        sidebarTitle, sidebarSteps,
        currentQuestion, currentStage,
        showApplyToAll,
        step, questionData,
        summaryPrice, pricesValue, priceCurrency,
        summaryText2, isFetchingBill,
        differencePrices,
        urlBack, thumbnailScenes,

        canGoNext, canGoPrevious, applyToAllElements,

        goNext: onNext, goPrevious: onPrevious, onValuesChange, onQuestionDataChange,
        goToStage, goToSummary, goToCompose,
        onAddElementWithId,
        setApplyToAllElements

    } = useConfiguratorState();
    const modelName = useModelName();

    const dispatch = useAppDispatch();
    const scene = useAppSelector(sceneSelector);
    const mode = useAppSelector(modeSelector);
    const showElementChooser = useAppSelector(showElementChooserSelector);
    const addAttach = useAppSelector(addAttachSelector);
    const values = useAppSelector(selectedValuesSelector);
    const sceneValues = useAppSelector(currentSceneValuesSelector);
    const implicitValues = useAppSelector(selectedImplicitValuesSelector);
    const loading = useAppSelector(viewerLoadingSelector);
    const showDebug = useAppSelector(showDebugSelector);
    const content = useAppSelector(contentSelector);
    const isRedirectedFromSales = useAppSelector(isRedirectedFromSalesSelector)
    const stages = useAppSelector(stagesSelector);
    const { maxShowablePerCounter } = useAppSelector(countersUseSelector);
    const { compose: hasCompose } = useAppSelector(catalogSelector);
    const requiredAttachPending = useAppSelector(requiredAttachPendingSelector);
    
    const { messages } = useMessages2();
    const hasContent = content.groups.filter(g => g.scene === MAIN_SCENE).length > 0;

    const showWarningComposeDialog = () => {
        dispatch(showPopup({dialog: 'warningInfo'}))
    }

    const onStageClick = (stageId: string) => {
        if(!requiredAttachPending){
            switch (stageId) {
                case SUMMARY_STAGE:
                    goToSummary();
                    break;
                case COMPOSE_STAGE:
                    goToCompose();
                    break;
                default:
                    goToStage(stageId);
                    break;
            }
        } else {
            showWarningComposeDialog();
        }
    }

    const onCloseElementChooser = () => {
        dispatch(configuratorActions.closeElementChooser());
    }

    const onDeleteAllElements = () => {
        dispatch(showPopup({dialog: 'deleteConfirm'}))
    }

    const onAddElement = () => {
        dispatch(configuratorActions.startAddElement());
    }



    let sidebarContent;
    if(mode === 'compose') {
        sidebarContent = <>
            <SidebarScrollable>
                <ButtonBar className={styles.buttonBar} fill={true}>
                    <button className={styles.buttons} type="button" onClick={onAddElement}>
                        <div className={styles.summaryLabel}>{ messages?.build_add_element ?? '' }</div> 
                    </button>
                    <button className={styles.buttons} disabled={!(content.groups.length>0)} type="button" onClick={onDeleteAllElements}>
                        <div className={styles.summaryLabel}>{ messages?.build_clear_elements ?? '' }</div> 
                    </button>
                </ButtonBar>
            </SidebarScrollable>
            <SidebarFixed className={styles.sidebarFixed}>
            <div className={styles.directionButtonsBar}>
                { canGoNext && <DirectionButton onClick={!requiredAttachPending ? onNext : showWarningComposeDialog} type="next"/>}
            </div>
            { hasContent ?
                <SuggestedPrice className={blockSeparator} 
                                price={summaryPrice} currency={priceCurrency}
                                differencePrices={differencePrices}
                                onClick={goToSummary}
                                isLoading={isFetchingBill}/>
                : null
            }
        </SidebarFixed></>
    } else if(mode === 'options') {
        if(currentQuestion) {
            sidebarContent = <>
                { showApplyToAll ? 
                    <div>
                        <Checkbox checked={applyToAllElements} onChange={e => { setApplyToAllElements(e.target.checked)}}/>
                        <label>{ messages?.apply_for_all_pieces_button ?? '' }</label>                            
                    </div>
                    :
                    null
                }
                <SidebarScrollable>
                    <ConfiguratorQuestion question={currentQuestion} values={scene === MAIN_SCENE ? values : sceneValues } implicitValues={implicitValues}
                                          step={step} questionData={questionData}
                                          maxShowablePerCounter={maxShowablePerCounter}
                                          onValuesChange={onValuesChange} onQuestionDataChange={onQuestionDataChange}/>
                </SidebarScrollable>
                <SidebarFixed className={styles.sidebarFixed}>
                    <div className={styles.directionButtonsBar}>
                        { canGoPrevious && <DirectionButton onClick={onPrevious} type="prev"/>}
                        { canGoNext && <DirectionButton onClick={onNext} type="next"/>}
                    </div>
                    { hasContent ?
                        <SuggestedPrice className={blockSeparator} 
                            price={summaryPrice} currency={priceCurrency}
                            differencePrices={differencePrices}
                            onClick={goToSummary}
                            isLoading={isFetchingBill}/>
                        : null
                    }
                </SidebarFixed>
            </>
        }
        
    } else if(mode === 'summary') {
        sidebarContent= <Summary 
                            text={summaryText2} 
                            price={summaryPrice}
                            currency={priceCurrency}
                            modelName={modelName} 
                        />
    }

    const overlayContent = <>
       <ConfiguratorHeader modelName={modelName} className={cfgLayoutStyles.overlayHeader} urlBack={isRedirectedFromSales ? undefined : urlBack}/>     
        { showDebug ? <DebugValues className={cfgLayoutStyles.overlayDebug}/> : null }
    </>

    const overlayFooter = <>
        { (pricesValue && priceCurrency) ? <PricesCard pricesValue={pricesValue} currency={priceCurrency}/> : null }
        <ViewControl resetCameraBehaviour={hasCompose ? 'fit' : 'initial'} 
                     show2D={ (hasCompose ?? false) && scene === MAIN_SCENE}/>
    </>

    return <ConfiguratorLayout 
        overlayContent={overlayContent}
        overlayFooter={overlayFooter}
        sidebarTitle={sidebarTitle}
        sidebarSteps={sidebarSteps}
        sidebarContent={sidebarContent}
        loading={loading}>
        {
            (thumbnailScenes ?? []).filter(s => s.show).map((scene, index) => {
                return <div key={index} className={styles.thumbnailScene} style={{
                    top: scene.position[1], left: scene.position[0],
                    width: scene.size[0], height: scene.size[1]
                }}>
                    <span>{ scene.label }</span>
                </div>
            })
        }
        <ConfiguratorStages className={styles.stages} yourConfigDisabled={!hasContent} 
                currentStage={currentStage} stages={stages} hasComposeStage={hasCompose} 
                onClick={onStageClick} /> 


            
        { showElementChooser ? <ElementChooser className={styles.bottomDrawer}
                                               profile={addAttach?.profile}
                                               onSelectElement={onAddElementWithId}
                                               onClose={onCloseElementChooser}/> 
                             : null }
        <Popup/>
    </ConfiguratorLayout>

}

